<template>

<div class="frame">

	<div class="frame-art" :style="{ aspectRatio: String(aspect) }">

		<!-- EXPERIMENT: a unified grid of art + hint tiles. Each revealed cell is
		     either clean art, or a HINT tile: the art beneath, blurred, with the
		     fact overlaid. On finish every tile clears to clean art. -->
		<div class="frame-grid">

			<div v-for="n in 16" :key="n - 1" class="frame-cell">

				<span
					v-if="revealed(n - 1) && !loaded[n - 1]"
					class="frame-loading"
				></span>

				<img
					v-if="revealed(n - 1)"
					class="frame-slice"
					:class="{ 'is-loaded': loaded[n - 1] }"
					:src="tileUrl(n - 1)"
					alt=""
					v-on:load="onSliceLoad($event, n - 1)"
				/>

				<!-- hint overlay: label + value on the (server-pixelated) art -->
				<div v-if="isHint(n - 1) && loaded[n - 1] && hintFor(n - 1)" class="frame-hint">
					<span class="frame-hint-label">{{ hintFor(n - 1).label }}</span>
					<span class="frame-hint-value">{{ hintFor(n - 1).value }}</span>
				</div>

				<span class="frame-cover" :class="{ 'is-lifted': revealed(n - 1) }"></span>

			</div>

		</div>

	</div>

</div>

</template>

<script>

/*
	The box-art frame — EXPERIMENT build (unified art + hint tiles).

	The server ships an ordered 16-tile list (today.tiles); each entry is either
	{ cell, type:'art' } or { cell, type:'hint', label, value, blur }. The reveal
	count (game.tilesRevealed) walks that list one per guess. A revealed art cell
	shows its clean slice; a revealed HINT cell shows the SAME slice blurred (by
	the tile's `blur` px) with the fact overlaid — the art underneath is never
	destroyed. On win/loss (finished) or reveal-all, every cell clears to clean
	art with no blur or text.

	Slices come from the gated per-tile endpoint; the grid stays blind (covered
	cells look identical) until each is uncovered.
*/

import identity from '../../service/identity'
import api from '../../service/api'

export default {

	props: {
		revealAll: {
			type: Boolean,
			default: false
		}
	},

	data: function() {

		return {
			loaded: new Array(16).fill(false),
			sliceAspect: null
		}

	},

	computed: {

		today: function() {

			return this.$store.getters['game/today']

		},

		puzzleDate: function() {

			return (this.today && this.today.date) || this.$store.state.game.date || ''

		},

		tilesRevealed: function() {

			return this.$store.getters['game/tilesRevealed']

		},

		finished: function() {

			return this.$store.getters['game/finished']

		},

		// on finish (or reveal-all) every tile clears to clean art
		cleanMode: function() {

			return this.revealAll || this.finished

		},

		// map of revealed cell index -> tile object (the first tilesRevealed of
		// the ordered list)
		cellMap: function() {

			var map = {}
			var tiles = (this.today && this.today.tiles) || []
			var n = this.tilesRevealed

			for (var i = 0; i < n && i < tiles.length; i++) {

				map[tiles[i].cell] = tiles[i]

			}

			return map

		},

		aspect: function() {

			var art = this.today && this.today.art

			if (art && art.w && art.h) return art.w / art.h

			return this.sliceAspect || 0.72

		}

	},

	watch: {

		today: function() {

			this.loaded = new Array(16).fill(false)
			this.sliceAspect = null

		}

	},

	methods: {

		revealed: function(cell) {

			return this.cleanMode || !!this.cellMap[cell]

		},

		isHint: function(cell) {

			return !this.cleanMode && this.cellMap[cell] && this.cellMap[cell].type === 'hint'

		},

		// the hint { label, value } for a hint cell, taken from the per-guess
		// hints (delivered server-side as each hint tile is revealed)
		hintFor: function(cell) {

			var tile = this.cellMap[cell]

			if (!tile || tile.type !== 'hint') return null

			var hints = this.$store.getters['game/hints']

			return hints[tile.hintIndex] || null

		},

		tileUrl: function(cell) {

			if (!this.puzzleDate) return ''

			var url = api.image('tile/' + this.puzzleDate + '/' + cell + '.jpg?v=nc')

			var token = identity.session()

			if (token) url += '&auth=' + encodeURIComponent(token)

			return url

		},

		onSliceLoad: function(event, index) {

			this.$set(this.loaded, index, true)

			var img = event.target

			if (img && img.naturalWidth && img.naturalHeight) {

				this.sliceAspect = img.naturalWidth / img.naturalHeight

			}

		}

	}

}

</script>

<style scoped>

.frame {
	position: relative;
	padding: 14px;
	background: #fbf8f1;
	border: 1px solid var(--hairline);
	border-radius: 6px;
	box-shadow: 0 10px 30px rgba(40, 34, 24, 0.07);
}

.frame-art {
	position: relative;
	width: 100%;
	aspect-ratio: 0.72;
	border-radius: var(--radius-sm);
	overflow: hidden;
	background: var(--tile);
}

.frame-grid {
	position: absolute;
	inset: 0;
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	grid-template-rows: repeat(4, 1fr);
}

.frame-cell {
	position: relative;
	overflow: hidden;
}

.frame-loading {
	position: absolute;
	inset: 0;
	background: linear-gradient(100deg, #e4ddcf 30%, #f3eee3 50%, #e4ddcf 70%);
	background-size: 220% 100%;
	animation: frame-shimmer 1.2s ease-in-out infinite;
}

.frame-slice {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: fill;
	display: block;
	opacity: 0;
	transition: opacity 250ms ease, filter 400ms ease;
}

.frame-slice.is-loaded {
	opacity: 1;
}

/* hint tile: the fact over the blurred art */
.frame-hint {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 2px;
	padding: 5px 6px;
	text-align: center;
	background: rgba(28, 26, 22, 0.34);
	/* never let a long value (e.g. 3 mechanics) spill outside the tile */
	overflow: hidden;
}

.frame-hint-label {
	font-family: var(--font-mono);
	font-size: 8.5px;
	font-weight: 600;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: #fff;
	line-height: 1.1;
	/* match the value's legibility over busy/pixelated art */
	text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
}

.frame-hint-value {
	font-family: var(--font-body);
	font-size: 11px;
	font-weight: 600;
	color: #fff;
	line-height: 1.2;
	text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
	/* wrap long values and clamp to the tile so nothing overflows its edges */
	max-width: 100%;
	overflow-wrap: anywhere;
	word-break: break-word;
	display: -webkit-box;
	-webkit-line-clamp: 5;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.frame-cover {
	position: absolute;
	inset: 0;
	background: #efe9dc;
	box-shadow: inset 0 0 0 0.5px #e0d8c8;
	opacity: 1;
	transition: opacity 400ms ease;
	z-index: 2;
}

.frame-cover.is-lifted {
	opacity: 0;
	pointer-events: none;
}

@keyframes frame-shimmer {
	0% { background-position: 180% 0; }
	100% { background-position: -80% 0; }
}

@media (prefers-reduced-motion: reduce) {

	.frame-cover,
	.frame-slice {
		transition: none;
	}

	.frame-loading {
		animation: none;
	}

}

</style>
